import { FeedItemDto } from '@act/shared/data-transfer-objects';
import { Button, IconLibrary, SmartContentDisplay } from '@act/storybook';
import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import { hospitalizationsSliceSelectors } from 'redux/slices/hospitalizations.slice';
import { surveysSelectors } from 'redux/slices/surveys.slice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import InfoCard from 'toolkit/info-card/info-card';
import { timeFormatter } from '@act/shared/utilities';
import { LinkFormatter } from 'utilities/formatters/link-formatter';
import useHospitalizationDialog from 'react-components/dialog-forms/hooks/use-hospitalizations-dialog';
import { HtmlDisplayerWithHyperlinks } from 'react-components/components/HtmlDisplayerWithHyperlinks';
import { DateTime } from 'luxon';
import { TaggableChip } from '@act/storybook';
import { SurveyDrawer } from 'react-components/dialog-forms/components/SurveyDrawer';

const HospitalizationFeedCard = ({
  feedItem,
  shouldHighlight,
}: {
  feedItem: FeedItemDto;
  shouldHighlight?: boolean;
}) => {
  const { createCall } = useInteractionsDialog();
  const openHospitalizationDialog = useHospitalizationDialog();
  const [isSurveyDrawerOpen, setIsSurveyDrawerOpen] = useState(false);

  const hospitalization =
    hospitalizationsSliceSelectors.useSelectHospitalizationById(
      feedItem.externalItemId
    );

  const member = membersAppSelectors.useSelectMemberById(
    hospitalization?.memberId
  );

  const assignedSurvey = surveysSelectors.useSelectAssignedSurveyById(
    hospitalization?.assignedSurveyId
  );

  if (!hospitalization) {
    return null;
  }

  const menuItems = useMemo(() => {
    if (!assignedSurvey) {
      return [];
    }

    return [
      {
        title: 'Complete Now',
        handler: () => {
          setIsSurveyDrawerOpen(true);
        },
      },
    ];
  }, [hospitalization.assignedSurveyId, assignedSurvey]);

  return (
    <>
      <InfoCard
        subject={
          <>
            Hospitalization{' '}
            {hospitalization.followUpType && (
              <TaggableChip isTag color="neutral" label="Behavioral Health" />
            )}
            {!hospitalization.followUpType && (
              <TaggableChip isTag color="neutral" label="Medical" />
            )}
          </>
        }
        variant="hospitalization"
        shouldHighlight={shouldHighlight}
        startTime={feedItem.timestamp}
        expandableHeaderLabel="Connection Details"
        menuItems={menuItems}
        primaryOption={
          <Button
            variant="text"
            onClick={() =>
              openHospitalizationDialog({
                hospitalization,
                user: member,
              })
            }
          >
            Edit
          </Button>
        }
        secondaryOption={
          <Button variant="text" onClick={() => createCall({ user: member })}>
            Call now
          </Button>
        }
      >
        <SmartContentDisplay maxHeight={225}>
          <div className="info-section">
            <div className="info">
              <Typography variant="labelMedium" fontWeight="bold" component="p">
                Admitting Hospital
              </Typography>
              <Typography variant="bodyMedium" component="p">
                {hospitalization.hospital ? (
                  <a
                    href={LinkFormatter.googleSearchUrl(
                      hospitalization.hospital
                    )}
                    target="_blank"
                  >
                    {hospitalization.hospital}
                  </a>
                ) : (
                  'Not provided'
                )}
              </Typography>
            </div>
            {hospitalization.admitComplaint && (
              <div className="info">
                <Typography
                  variant="labelMedium"
                  fontWeight="bold"
                  component="p"
                >
                  Admitting Diagnosis
                </Typography>
                <Typography variant="bodyMedium" component="p">
                  {hospitalization.admitComplaint}
                </Typography>
              </div>
            )}
            {hospitalization.admitDate && (
              <div className="info">
                <Typography
                  variant="labelMedium"
                  fontWeight="bold"
                  component="p"
                >
                  Admit Date
                </Typography>
                <Typography variant="bodyMedium" component="p">
                  {timeFormatter.epochToHumanReadableDate({
                    date: DateTime.fromFormat(
                      hospitalization.admitDate,
                      'yyyy-MM-dd'
                    ).toMillis(),
                  })}
                </Typography>
              </div>
            )}
            {hospitalization.dischargeDate && (
              <div className="info">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="labelMedium"
                    fontWeight="bold"
                    component="p"
                  >
                    Discharge Date{' '}
                  </Typography>
                  {!hospitalization.isAssumed && (
                    <IconLibrary iconName="SealCheck" />
                  )}
                </Box>
                <Typography variant="bodyMedium" component="p">
                  {timeFormatter.epochToHumanReadableDate({
                    date: DateTime.fromFormat(
                      hospitalization.dischargeDate,
                      'yyyy-MM-dd'
                    ).toMillis(),
                  })}
                </Typography>
              </div>
            )}
            {/* Show only if followUpType is behavioral health */}
            {hospitalization.followUpType && (
              <div className="info">
                <Typography
                  variant="labelMedium"
                  fontWeight="bold"
                  component="p"
                >
                  Enrolled in Behavioral Health
                </Typography>
                <Typography variant="bodyMedium" component="p">
                  {hospitalization.isEnrolledInBehavioralHealth
                    ? 'True'
                    : 'False'}
                </Typography>
              </div>
            )}

            {hospitalization.expectedSurveyCompletion && (
              <div className="info">
                <Typography
                  variant="labelMedium"
                  fontWeight="bold"
                  component="p"
                >
                  Expected Survey Completion
                </Typography>
                <Typography variant="bodyMedium" component="p">
                  {timeFormatter.epochToHumanReadableDate({
                    date: DateTime.fromFormat(
                      hospitalization.expectedSurveyCompletion,
                      'yyyy-MM-dd'
                    ).toMillis(),
                  })}
                </Typography>
              </div>
            )}
          </div>
          {hospitalization.note && (
            <div className="notes">
              <Typography
                variant="labelMedium"
                fontWeight="bold"
                component="h3"
              >
                Notes
              </Typography>
              {HtmlDisplayerWithHyperlinks({
                note: hospitalization.note,
                displayMessage: 'Links in this note:',
              })}
            </div>
          )}
        </SmartContentDisplay>
      </InfoCard>
      {assignedSurvey && (
        <SurveyDrawer
          open={isSurveyDrawerOpen}
          onClose={() => setIsSurveyDrawerOpen(false)}
          survey={assignedSurvey}
          isReviewMode={false}
        />
      )}
    </>
  );
};

export default HospitalizationFeedCard;
